import { create } from 'zustand';
import { marketingApi } from '../services/marketing-api';
import type { MarketingTrend, MarketingBreakdownItem, FunnelData, EfficiencyDay, AudienceDemographics, MessagingOverview, UnifiedFunnel, FunnelTrendDay, PancakeTagCounts } from '../types/marketing';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

type TrendGroupBy = 'day' | 'month';

interface MarketingState {
  fromDate: string;
  toDate: string;
  trendGroupBy: TrendGroupBy;

  marketingTrend: MarketingTrend[];
  byCampaign: MarketingBreakdownItem[];
  byAccount: MarketingBreakdownItem[];
  byActionType: MarketingBreakdownItem[];
  funnel: FunnelData | null;
  efficiencyTrend: EfficiencyDay[];
  audience: AudienceDemographics | null;
  messaging: MessagingOverview | null;
  unifiedFunnel: UnifiedFunnel | null;
  funnelTrend: FunnelTrendDay[];
  pancakeTagCounts: PancakeTagCounts | null;

  reportFrom: string;
  reportTo: string;
  fetchedKey: string | null;

  isLoading: boolean;
  error: string | null;

  setDateRange: (from: string, to: string) => void;
  setTrendGroupBy: (g: TrendGroupBy) => void;
  fetchDashboardData: (force?: boolean) => Promise<void>;
  fetchTrend: (groupBy: TrendGroupBy) => Promise<void>;
}

const now = new Date();
const lastMonth = subMonths(now, 1);
const defaultFrom = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
const defaultTo = format(endOfMonth(lastMonth), 'yyyy-MM-dd');

export const useMarketingStore = create<MarketingState>((set, get) => ({
  fromDate: defaultFrom,
  toDate: defaultTo,
  trendGroupBy: 'day' as TrendGroupBy,

  marketingTrend: [],
  byCampaign: [],
  byAccount: [],
  byActionType: [],
  funnel: null,
  efficiencyTrend: [],
  audience: null,
  messaging: null,
  unifiedFunnel: null,
  funnelTrend: [],
  pancakeTagCounts: null,

  reportFrom: defaultFrom,
  reportTo: defaultTo,
  fetchedKey: null,

  isLoading: false,
  error: null,

  setDateRange: (from, to) => {
    set({ fromDate: from, toDate: to });
  },

  setTrendGroupBy: (g) => {
    set({ trendGroupBy: g });
  },

  fetchTrend: async (groupBy: TrendGroupBy) => {
    const { fromDate, toDate } = get();
    try {
      const marketingTrend = await marketingApi.getMarketingTrend({ from: fromDate, to: toDate, groupBy });
      set({ marketingTrend, trendGroupBy: groupBy });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch trend data' });
    }
  },

  fetchDashboardData: async (force = false) => {
    const { fromDate, toDate, fetchedKey, trendGroupBy } = get();
    const currentKey = `${fromDate}|${toDate}`;

    if (!force && fetchedKey === currentKey) return;

    set({ isLoading: true, error: null });

    try {
      const params = { from: fromDate, to: toDate };
      const [
        marketingTrend,
        byCampaign,
        byAccount,
        byActionType,
        funnel,
        efficiencyTrend,
        audience,
        messaging,
        unifiedFunnel,
        funnelTrend,
        pancakeTagCounts,
      ] = await Promise.all([
        marketingApi.getMarketingTrend({ ...params, groupBy: trendGroupBy }),
        marketingApi.getByCampaign(params),
        marketingApi.getByAccount(params),
        marketingApi.getByActionType(params),
        marketingApi.getFunnel(params),
        marketingApi.getEfficiencyTrend(params),
        marketingApi.getAudienceDemographics(),
        marketingApi.getMessagingOverview(params),
        marketingApi.getUnifiedFunnel(params),
        marketingApi.getFunnelTrend(params),
        marketingApi.getPancakeTagCounts(params),
      ]);

      set({
        marketingTrend,
        byCampaign,
        byAccount,
        byActionType,
        funnel,
        efficiencyTrend,
        audience,
        messaging,
        unifiedFunnel,
        funnelTrend,
        pancakeTagCounts,
        isLoading: false,
        fetchedKey: currentKey,
        reportFrom: fromDate,
        reportTo: toDate,
      });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch marketing data', isLoading: false });
    }
  },
}));
